import * as React from 'react';
import { useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Button } from 'semantic-ui-react';
import SemanticDataTable, { SemanticDataTableColumn, SemanticDataTableHandle } from '../../shared/semanticTable/SemanticDataTable';
import { RowData } from './GtfsEditorForm';

export interface GtfsTableHandle {
    setFilterValue: (fieldName: string, data: string) => void;
    setMultiplyValueFilter: (fieldName: string, data: string[]) => void;
    clearFilters: () => void;
    fileName: string;
}

export interface Props {
    fileName: string;
    data: RowData[];
    onCellClick: (rowData: RowData, fieldName: string, value: string) => void;
}

const styles = {
    tableTitle: {
        fontFamily: 'monospace',
        fontSize: '18px',
        fontWeight: 700,
        margin: '16px 0px 4px 0px',
    } as React.CSSProperties,
    toggleButton: {
        margin: '4px 0px 12px 0px',
    } as React.CSSProperties,
};

const hiddenColumnsByTable: Record<string, string[]> = {
    'trips.txt': ['wheelchair_accessible', 'bikes_allowed', 'end_time', 'start_time'],
};

const clickableColumns = new Set(['trip_id', 'service_id', 'route_id', 'stop_id', 'stop_bays']);

function getColumnStyle(columnId: string): React.CSSProperties {
    const narrowColumns = new Set(['route_id', 'service_id', 'stop_id', 'stop_bays', 'trip_id']);
    const widerColumns = new Set(['trip_headsign', 'trip_short_name', 'shape_id', 'times']);
    const width = narrowColumns.has(columnId)
        ? 180
        : widerColumns.has(columnId)
            ? 220
            : 160;

    return {
        width,
        minWidth: width,
        maxWidth: width,
    };
}

function buildColumnOrder(fileName: string, sampleRow: RowData | undefined) {
    const hiddenColumns = new Set<string>(hiddenColumnsByTable[fileName] ?? []);
    const columnIds = Object.keys(sampleRow ?? {}) as string[];
    const visibleColumnIds = columnIds.filter((columnId) => !hiddenColumns.has(columnId));

    if (fileName === 'stops.txt' && !visibleColumnIds.includes('stop_bays')) {
        visibleColumnIds.splice(Math.min(1, visibleColumnIds.length), 0, 'stop_bays');
    }

    if (fileName === 'trips.txt' && !visibleColumnIds.includes('times')) {
        visibleColumnIds.splice(Math.min(1, visibleColumnIds.length), 0, 'times');
    }

    return visibleColumnIds;
}

const GtfsTable = React.forwardRef<GtfsTableHandle, Props>(({
    fileName,
    data,
    onCellClick,
}, ref) => {
    const [isExpanded, setIsExpanded] = useState(true);
    const tableRef = useRef<SemanticDataTableHandle>(null);

    const columns = useMemo<SemanticDataTableColumn<RowData>[]>(() => buildColumnOrder(fileName, data[0]).map((columnId) => ({
        id: columnId,
        title: columnId,
        headerStyle: getColumnStyle(columnId),
        cellStyle: getColumnStyle(columnId),
        render: (row) => {
            const value = row[columnId] ?? '';
            if (!clickableColumns.has(columnId) || value.length === 0) {
                return value;
            }

            return (
                <button
                    type="button"
                    className="semanticTableClickable"
                    onClick={(event) => {
                        event.stopPropagation();
                        onCellClick(row, columnId, value);
                    }}>
                    {value}
                </button>
            );
        },
        getSortValue: (row) => row[columnId] ?? '',
        getFilterValue: (row) => row[columnId] ?? '',
        filter: {
            type: 'text',
            placeholder: 'Filter...',
            matchMode: 'contains',
        },
    })), [data, fileName, onCellClick]);

    useImperativeHandle(ref, () => ({
        clearFilters() {
            tableRef.current?.clearFilters();
        },
        fileName,
        setFilterValue(fieldName, value) {
            tableRef.current?.setFilterValue(fieldName, value);
        },
        setMultiplyValueFilter(fieldName, values) {
            tableRef.current?.setFilterValues(fieldName, values);
        },
    }), [fileName]);

    return (
        <div>
            <p style={styles.tableTitle}>{fileName}</p>
            <Button
                style={styles.toggleButton}
                onClick={() => setIsExpanded((previousState) => !previousState)}>
                display {isExpanded ? '-' : '+'}
            </Button>
            {isExpanded && (
                <SemanticDataTable<RowData>
                    ref={tableRef}
                    columns={columns}
                    data={data}
                    rowKey={(_row, rowIndex) => `${fileName}-${rowIndex}`}
                    className="semanticTable"
                    emptyMessage="No records match the current filters."
                    height={400}
                    fixed={true}
                    singleLine={false}
                />
            )}
        </div>
    );
});

export default GtfsTable;
